import React, { useState, useEffect, useCallback } from 'react';

// Import components
import { ErrorBoundary, OfflineNotification } from './components/common';
import Navigation from './components/navigation/Navigation';
import { PropertyInfo, Restaurants, HiddenGems, SafetyInfo, GuestList, InstallBanner, Schedule, Activities, Weather, Photos } from './components/sections';
import BeachFlagModal from './components/modals/BeachFlagModal';
import AppStyles from './components/app/AppStyles';
import WaveBackground from './components/app/WaveBackground';

// Import hooks
import useTheme from './hooks/useTheme';
import useWeatherData from './hooks/useWeatherData';
import useBeachFlag from './hooks/useBeachFlag';
import useCountdown from './hooks/useCountdown';
import useSwipeGesture from './hooks/useSwipeGesture';

// Import constants
import { TRIP_DATA, NAVIGATION_SECTIONS } from './constants/tripData';
import { CONFIG } from './constants/config';

// Import utilities
import { removeFromStorage } from './utils/localStorage';
import { fetchWithTimeout } from './utils/api';

const App = () => {
  const { weather, tides, waterTemp, waterTempStation, currentWaterLevel, moonData, loading: loadingWeather, error: weatherError, refetch: fetchWeatherData } = useWeatherData();
  const { theme, toggleTheme, themeMode, enableAutoTheme, disableAutoTheme } = useTheme(
    weather?.daily?.sunrise?.[0], 
    weather?.daily?.sunset?.[0]
  );
  const countdown = useCountdown();
  const { flagStatus, showFlagUpdatePrompt, setShowFlagUpdatePrompt, updateFlagStatus } = useBeachFlag();
  
  const [activeSection, setActiveSection] = useState('schedule');
  
  // Handle swipe gestures for section navigation
  const sectionKeys = NAVIGATION_SECTIONS.map(section => section.id);
  const currentIndex = sectionKeys.indexOf(activeSection);
  
  const handleSwipeLeft = useCallback(() => {
    if (currentIndex < sectionKeys.length - 1) {
      setActiveSection(sectionKeys[currentIndex + 1]);
    }
  }, [currentIndex, sectionKeys]);
  
  const handleSwipeRight = useCallback(() => {
    if (currentIndex > 0) {
      setActiveSection(sectionKeys[currentIndex - 1]);
    }
  }, [currentIndex, sectionKeys]);
  
  const swipeHandlers = useSwipeGesture(handleSwipeLeft, handleSwipeRight);
  
  // Re-run animation observer when section changes
  const runAnimationObserver = useCallback(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return observer;
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // PWA install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    });
  }, []);
  
  // Run animation observer when active section changes
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      const observer = runAnimationObserver();
      
      // Cleanup function to disconnect observer
      return () => {
        observer.disconnect();
      };
    }, 100);
    
    return () => clearTimeout(timer);
  }, [activeSection, runAnimationObserver]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstallable(false);
      }
      setDeferredPrompt(null);
    }
  };

  // Test API connection function
  const testApiConnection = async () => {
    try {
      const testUrl = 'https://api.open-meteo.com/v1/forecast?latitude=29.6705&longitude=-85.3548&current=temperature_2m&temperature_unit=fahrenheit';
      const response = await fetchWithTimeout(testUrl, 5000);
      
      if (!response.ok) {
        const errorText = await response.text();
        alert(`API Connection failed: ${response.status} - ${errorText}`);
        return;
      }
      
      const data = await response.json();
      alert(`API Connection successful! Temperature: ${data.current?.temperature_2m}°F\nAPI Version: ${data.generationtime_ms ? 'v1' : 'unknown'}`);
      
      // Now try full fetch
      await fetchWeatherData();
    } catch (error) {
      alert(`API Connection failed: ${error.message}`);
    }
  };

  // Clear cache and retry
  const clearCacheAndRetry = () => {
    removeFromStorage(CONFIG.CACHE_KEY);
    fetchWeatherData();
  };

  // Use imported constants
  const tripData = TRIP_DATA;
  const sections = NAVIGATION_SECTIONS;

  // Component mapping for sections
  const sectionComponents = {
    schedule: (
      <Schedule
        schedule={tripData.schedule}
        weather={weather}
        tides={tides}
        waterTemp={waterTemp}
        moonData={moonData}
        flagStatus={flagStatus}
        loadingWeather={loadingWeather}
        weatherError={weatherError}
        onFetchWeather={fetchWeatherData}
        onUpdateFlag={() => setShowFlagUpdatePrompt(true)}
        onTestApi={testApiConnection}
        onClearCache={clearCacheAndRetry}
      />
    ),
    weather: (
      <Weather 
        weather={weather}
        tides={tides}
        waterTemp={waterTemp}
        waterTempStation={waterTempStation}
        currentWaterLevel={currentWaterLevel}
        moonData={moonData}
        loadingWeather={loadingWeather}
        weatherError={weatherError}
        onFetchWeather={fetchWeatherData}
        flagStatus={flagStatus}
        onUpdateFlag={() => setShowFlagUpdatePrompt(true)}
      />
    ),
    property: (
      <PropertyInfo 
        property={tripData.property}
        reminders={tripData.reminders}
        emergency={tripData.emergency}
      />
    ),
    restaurants: <Restaurants restaurants={tripData.restaurants} />,
    hidden: <HiddenGems hiddenGems={tripData.hiddenGems} />,
    activities: <Activities activities={tripData.activities} moonData={moonData} />,
    safety: <SafetyInfo safety={tripData.safety} />,
    guests: <GuestList guests={tripData.guests} />,
    photos: <Photos />
  };

  const renderContent = () => {
    return sectionComponents[activeSection] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <a href="#main-content" className="skip-nav">
        Skip to main content
      </a>
      <AppStyles />
      <WaveBackground />
      <OfflineNotification />
      
      <Navigation
        sections={sections}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        countdown={countdown}
        theme={theme}
        onToggleTheme={toggleTheme}
        themeMode={themeMode}
        onEnableAutoTheme={enableAutoTheme}
        onDisableAutoTheme={disableAutoTheme}
      />

      <main 
        id="main-content" 
        className="max-w-6xl mx-auto p-4 pb-20"
        {...swipeHandlers}
      >
        <div className="section-transition page-enter" key={activeSection}>
          {renderContent()}
        </div>
        {/* Swipe hint for mobile */}
        <div className="swipe-hint md:hidden text-center text-sm text-gray-500 dark:text-gray-400">
          Swipe left or right to navigate
        </div>
      </main>

      <InstallBanner isInstallable={isInstallable} onInstall={handleInstall} />

      <BeachFlagModal
        show={showFlagUpdatePrompt}
        onClose={() => setShowFlagUpdatePrompt(false)}
        onUpdateFlag={updateFlagStatus}
        flagStatus={flagStatus}
        weather={weather}
      />
    </div>
  );
};

export default App;